import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Api } from '../adapters/Api';
import type { BotSummary } from '../domain/types';
import { MAX_INSTANCES } from '../domain/types';
import { useI18n } from '../i18n';
import { ActivityFeed, recentCityEvents } from './ActivityFeed';
import { Dashboard } from './Dashboard';
import { OperationModal } from './OperationModal';
import { Panel, StatusPill } from './components';
import { describeStatus, formatRelative, stateLabel } from './status';

// Descarga del cliente (2026-07-27, dueño): vive AQUÍ, en la vista de flota —
// un usuario nuevo con 0 bots nunca llegaba al Dashboard por-bot donde estaba
// antes y no podía descargar el cliente. La versión se saca del nombre del ZIP
// (whitebot-client-setup-X.Y.Z.zip) para mostrarla junto al botón.
const CLIENT_DOWNLOAD_URL = (import.meta.env.VITE_CLIENT_DOWNLOAD_URL as string | undefined) || '';
const CLIENT_DOWNLOAD_VERSION = CLIENT_DOWNLOAD_URL.match(/(\d+\.\d+\.\d+)/)?.[1] ?? '';

// Vista de FLOTA (multi-instancia 0.6, doc 11): una tarjeta por bot (fila de
// downloadable_bots del usuario), contador N/5, iniciar/parar todo y eliminar.
// Click en una tarjeta → el Dashboard de siempre scoped a esa instancia.
// Con UN solo bot se entra directo (UX mono-instancia intacta), con botón de
// volver para descubrir la flota. Las tarjetas aparecen solas: cada consola
// nueva del cliente reporta y su fila nace en el primer report.

export function Fleet({ api }: { api: Api }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [autoEntered, setAutoEntered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<BotSummary | null>(null);

  const botsQuery = useQuery({
    queryKey: ['botFleet'],
    queryFn: () => api.listBots(),
    refetchInterval: 3000
  });
  const bots = botsQuery.data ?? [];

  // Badge "🛡️ protegido" en la tarjeta del bot atacado (0.6.28). Misma query
  // que el feed de Actividad — react-query la deduplica por la key.
  const historyQuery = useQuery({
    queryKey: ['shieldWatchHistory'],
    queryFn: () => api.dlShieldWatchHistory(15),
    refetchInterval: 20000,
    retry: false
  });
  const cityEvents = recentCityEvents(historyQuery.data);
  // El badge se puede CERRAR con su ✕ (petición del dueño 2026-08-05): se
  // recuerda por (bot → sello del ataque) en localStorage, así un ataque
  // NUEVO vuelve a mostrarlo. La ventana de 24 h queda como caducidad de
  // respaldo; el evento sigue en el feed de Actividad (la ✕ quita el aviso,
  // no la historia).
  const [dismissed, setDismissed] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem('white-bot-dl:cityBadgeSeen');
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
    } catch {
      return {};
    }
  });
  function dismissBadge(instanceId: string, stamp: number) {
    setDismissed((prev) => {
      const next = { ...prev, [instanceId]: stamp };
      try {
        localStorage.setItem('white-bot-dl:cityBadgeSeen', JSON.stringify(next));
      } catch {
        /* localStorage lleno o bloqueado: el estado en memoria basta esta sesión */
      }
      return next;
    });
  }

  // Mono-instancia: directo al dashboard (una sola vez; "volver" no re-entra)
  if (!autoEntered && !selected && botsQuery.isSuccess && bots.length === 1) {
    setSelected(bots[0].instanceId);
    setAutoEntered(true);
  }

  const startStopAll = useMutation({
    mutationFn: async (desired: 'run' | 'stop') => {
      for (const bot of bots) {
        // los módulos actuales de cada bot se respetan (solo cambia desired)
        const status = await api.getBotStatus(bot.instanceId);
        await api.setDesired(desired, status.modules, bot.instanceId);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['botFleet'] })
  });

  const deleteMutation = useMutation({
    mutationFn: (instanceId: string) => api.deleteBot(instanceId),
    onSuccess: () => {
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['botFleet'] });
    }
  });

  if (selected) {
    return <Dashboard api={api} instanceId={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <main className="fleet">
      {confirmDelete && (
        <OperationModal
          active={deleteMutation.isPending}
          tone="warning"
          titleId="fleet-delete-title"
          eyebrow={t('fleet.title')}
          title={t('fleet.deleteConfirmTitle', { name: botName(confirmDelete, t) })}
          body={t('fleet.deleteConfirmBody')}
          actions={[
            { label: t('fleet.deleteCancel'), onClick: () => setConfirmDelete(null) },
            {
              label: t('fleet.deleteConfirm'),
              onClick: () => deleteMutation.mutate(confirmDelete.instanceId),
              disabled: deleteMutation.isPending,
              primary: true
            }
          ]}
        />
      )}

      <div className="fleet-header">
        <h1>{t('fleet.title')}</h1>
        <span className="fleet-counter">{t('fleet.counter', { n: String(bots.length), max: String(MAX_INSTANCES) })}</span>
        <div className="fleet-actions">
          <button
            type="button"
            className="primary"
            disabled={startStopAll.isPending || bots.length === 0}
            onClick={() => startStopAll.mutate('run')}
          >
            {t('fleet.startAll')}
          </button>
          <button
            type="button"
            className="danger"
            disabled={startStopAll.isPending || bots.length === 0}
            onClick={() => startStopAll.mutate('stop')}
          >
            {t('fleet.stopAll')}
          </button>
        </div>
      </div>

      {bots.length === 0 && (
        <div className="fleet-empty">
          <p>{t('fleet.empty')}</p>
        </div>
      )}

      <div className="fleet-grid">
        {bots.map((bot) => (
          <article key={bot.instanceId} className="fleet-card" onClick={() => setSelected(bot.instanceId)}>
            <header>
              <strong>{botName(bot, t)}</strong>
              <StatusPill kind={bot.running ? 'ok' : ''}>{stateLabel(bot.running ? bot.state : 'idle', t)}</StatusPill>
            </header>
            <p className="fleet-card-activity">{describeStatus(bot, t)}</p>
            {(() => {
              const cityEvent = cityEvents.get(bot.instanceId);
              if (!cityEvent || (dismissed[bot.instanceId] ?? 0) >= cityEvent.stamp) return null;
              const danger = cityEvent.state === 'attack_halted';
              return (
                <p className={`fleet-card-shield ${danger ? 'fleet-card-shield-danger' : ''}`}>
                  {danger ? '🚨' : '🛡️'}{' '}
                  {t(danger ? 'activity.attackHalted' : 'activity.protected')}
                  {' · '}
                  {formatRelative(cityEvent.at, t)}
                  <button
                    type="button"
                    className="fleet-card-shield-x"
                    aria-label={t('shieldWatch.captureClose')}
                    title={t('shieldWatch.captureClose')}
                    onClick={(event) => {
                      event.stopPropagation();
                      dismissBadge(bot.instanceId, cityEvent.stamp);
                    }}
                  >
                    ✕
                  </button>
                </p>
              );
            })()}
            <footer>
              <span className="muted">
                {t('fleet.lastReport')}: {formatRelative(bot.lastReportAt, t)}
                {bot.clientVersion ? ` · v${bot.clientVersion}` : ''}
              </span>
              <button
                type="button"
                className="fleet-delete"
                onClick={(event) => {
                  event.stopPropagation();
                  setConfirmDelete(bot);
                }}
              >
                {t('fleet.delete')}
              </button>
            </footer>
          </article>
        ))}
      </div>

      {/* Actividad (0.6.28): la versión persistente de los correos — ataques a
          tu ciudad y cambios de la vigilancia, con su captura. Solo aparece si
          hay eventos. */}
      <ActivityFeed
        api={api}
        nameOf={(instanceId) => {
          const bot = bots.find((b) => b.instanceId === instanceId);
          return bot ? botName(bot, t) : instanceId.slice(0, 6);
        }}
      />

      {/* Descarga del cliente DESPUÉS de las tarjetas (dueño, 2026-07-27): los
          bots son lo primero; la descarga queda como bloque secundario debajo. */}
      {CLIENT_DOWNLOAD_URL && (
        <Panel title={t('dashboard.client')} className="fleet-download">
          <p className="fleet-download-body">{t('dashboard.clientBody')}</p>
          {/* El registro va ANTES y sin await: navegar a un ZIP no descarga la
              página (el navegador solo dispara la descarga), así que el insert
              termina en segundo plano y el clic no espera a la red. */}
          <button className="primary" onClick={() => {
            void api.logClientDownload(CLIENT_DOWNLOAD_VERSION);
            window.location.href = CLIENT_DOWNLOAD_URL;
          }}>
            ⬇ {t('dashboard.clientDownloadReady')}
            {CLIENT_DOWNLOAD_VERSION ? ` — v${CLIENT_DOWNLOAD_VERSION}` : ''}
          </button>
          <p className="fleet-download-hint">{t('dashboard.clientSmartscreen')}</p>
          <a className="fleet-download-guide" href="/guia" target="_blank" rel="noopener">
            {t('app.guideButton')}
          </a>
        </Panel>
      )}
    </main>
  );
}

export function botName(bot: BotSummary, t: ReturnType<typeof useI18n>['t']): string {
  if (bot.instanceLabel) return bot.instanceLabel;
  if (bot.instanceId === 'default') return t('fleet.defaultName');
  return `${t('fleet.defaultName')} ${bot.instanceId.slice(0, 4)}`;
}
